import React, { useState } from "react";
import { AppSettingHistoryEntry } from "../../../types/appSettings";
import appSettingsService from "../../../services/appSettingsService";
import { useToast } from "../../../contexts/ToastContext";
import ConfirmationModal from "../../../components/ConfirmationModal";
import { ChevronRight } from "lucide-react";

interface HistoryTabProps {
  scope: string;
  history: AppSettingHistoryEntry[];
  onRestored: () => void;
  isDarkMode: boolean;
}

const HistoryTab: React.FC<HistoryTabProps> = ({
  scope,
  history,
  onRestored,
  isDarkMode,
}) => {
  const toast = useToast();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [restoringIndex, setRestoringIndex] = useState<number | null>(null);
  const [confirmEntry, setConfirmEntry] = useState<{ entry: AppSettingHistoryEntry; index: number } | null>(null);

  const toggleRow = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const handleRestore = async (entry: AppSettingHistoryEntry, index: number) => {
    try {
      setRestoringIndex(index);
      setConfirmEntry(null);
      await appSettingsService.updateSettings(scope, entry.data);
      toast.success("Snapshot restored successfully");
      onRestored();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to restore snapshot";
      toast.error(message);
    } finally {
      setRestoringIndex(null);
    }
  };

  const borderClass = isDarkMode ? "border-rp-separator" : "border-gray-200";
  const surfaceClass = isDarkMode ? "bg-rp-surface" : "bg-white";
  const mutedClass = isDarkMode ? "text-rp-grey/40" : "text-gray-400";

  const formatValue = (val: unknown): React.ReactNode => {
    if (val === null) return <span className={`italic ${mutedClass}`}>null</span>;
    if (Array.isArray(val)) {
      if (val.length === 0) return <span className={mutedClass}>[ ]</span>;
      return <span className="text-rp-amber">[{val.join(", ")}]</span>;
    }
    return <span className="text-rp-amber">{String(val)}</span>;
  };

  return (
    <div className={`rounded-lg border ${borderClass} ${surfaceClass} p-6`}>
      <div className={`flex items-center justify-between mb-5 pb-4 border-b ${borderClass}`}>
        <div>
          <div className={`text-sm font-medium font-mono ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>
            {scope}
          </div>
          <div className={`text-xs mt-0.5 ${mutedClass}`}>Recent change history</div>
        </div>
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr className={`text-xs uppercase tracking-wider ${mutedClass}`}>
            <th className={`text-left py-2.5 px-3 border-b ${borderClass} w-7`}></th>
            <th className={`text-left py-2.5 px-3 border-b ${borderClass}`}>Date (UTC)</th>
            <th className={`text-left py-2.5 px-3 border-b ${borderClass}`}>User</th>
            <th className={`text-right py-2.5 px-3 border-b ${borderClass}`}></th>
          </tr>
        </thead>
        <tbody>
          {history.map((entry, i) => {
            const isExpanded = expandedIndex === i;
            const isCurrent = i === 0;
            return (
              <React.Fragment key={i}>
                <tr
                  onClick={() => toggleRow(i)}
                  className={`cursor-pointer transition-colors ${
                    isDarkMode ? "hover:bg-rp-surface-raised" : "hover:bg-gray-50"
                  }`}
                >
                  <td className={`py-2.5 px-3 border-b ${borderClass}`}>
                    <ChevronRight
                      className={`w-3 h-3 transition-transform ${isExpanded ? "rotate-90" : ""} ${mutedClass}`}
                    />
                  </td>
                  <td className={`py-2.5 px-3 border-b ${borderClass}`}>{entry.datetimeUtc}</td>
                  <td className={`py-2.5 px-3 border-b ${borderClass}`}>{entry.userId}</td>
                  <td className={`py-2.5 px-3 border-b ${borderClass} text-right`}>
                    {isCurrent ? (
                      <span className={`text-xs ${mutedClass}`}>current</span>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmEntry({ entry, index: i });
                        }}
                        disabled={restoringIndex === i}
                        className="text-xs text-rp-amber border border-rp-amber rounded px-2.5 py-1 hover:bg-rp-amber hover:text-black transition-colors disabled:opacity-50"
                      >
                        {restoringIndex === i ? "Restoring..." : "Restore"}
                      </button>
                    )}
                  </td>
                </tr>
                {isExpanded && (
                  <tr>
                    <td
                      colSpan={4}
                      className={`border-b ${borderClass} p-0`}
                    >
                      <div className={`py-3 px-4 pl-10 ${isDarkMode ? "bg-rp-surface-sunken" : "bg-gray-50"}`}>
                        <table className="w-full text-xs">
                          <thead>
                            <tr className={`uppercase tracking-wider ${mutedClass}`}>
                              <th className={`text-left py-1.5 px-2.5 border-b ${borderClass} font-semibold`}>Setting</th>
                              <th className={`text-left py-1.5 px-2.5 border-b ${borderClass} font-semibold`}>Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(entry.data).map(([key, val]) => (
                              <tr key={key}>
                                <td className={`py-1.5 px-2.5 border-b border-opacity-50 ${borderClass} font-mono w-56 ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>
                                  {key}
                                </td>
                                <td className={`py-1.5 px-2.5 border-b border-opacity-50 ${borderClass} font-mono`}>
                                  {formatValue(val)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {history.length === 0 && (
        <div className={`text-center py-8 text-sm ${mutedClass}`}>No history available for this scope.</div>
      )}

      <ConfirmationModal
        isOpen={confirmEntry !== null}
        onClose={() => setConfirmEntry(null)}
        onConfirm={() => {
          if (confirmEntry) handleRestore(confirmEntry.entry, confirmEntry.index);
        }}
        title="Restore snapshot"
        message={`This will overwrite the current settings for "${scope}" with the snapshot from ${confirmEntry?.entry.datetimeUtc ?? ""}. Are you sure?`}
        confirmText="Restore"
        isDestructive
        loading={restoringIndex !== null}
      />
    </div>
  );
};

export default HistoryTab;
